import { c as _c } from "react/compiler-runtime";
import { NavigationMenu as BaseNavigationMenu } from '@base-ui/react/navigation-menu';
import { menuPopupVariants } from '@systemeio/components';
import { forwardRef, type ComponentRef } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * Base UI accepts `className` as a plain string or as a function of the part's state. Merging the
 * navbar defaults has to keep both forms, so the function form is composed rather than flattened.
 */
type StatefulClassName<State> = string | ((state: State) => string | undefined) | undefined;
function mergeClassName<State>(base: string, className: StatefulClassName<State>) {
  if (typeof className === 'function') {
    return (state: State) => twMerge(base, className(state));
  }
  return twMerge(base, className);
}

/**
 * Base UI Navigation Menu parts carrying the navbar's popup chrome. Local to the dashboard: the
 * navigation bar is the only consumer, so this stays out of `@systemeio/components`.
 *
 * Only structure and behaviour live here. Row and trigger looks stay at the call site: navbar
 * triggers use `getItemClassName`, popup rows the library's own `menuItemVariants`.
 *
 * Hover is the primary open path and it is two-phase by design: while the menu is closed a trigger
 * waits for the pointer to rest (`delay`, 50ms), and once any sibling is open the rest requirement
 * drops to zero, so moving along the row switches panels instantly.
 */
function Root(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <BaseNavigationMenu.Root {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const List = forwardRef<ComponentRef<typeof BaseNavigationMenu.List>, NavigationMenu.List.Props>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = mergeClassName("list-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <BaseNavigationMenu.List ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
const Item = forwardRef<ComponentRef<typeof BaseNavigationMenu.Item>, NavigationMenu.Item.Props>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = mergeClassName("min-w-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <BaseNavigationMenu.Item ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
const Trigger = forwardRef<ComponentRef<typeof BaseNavigationMenu.Trigger>, NavigationMenu.Trigger.Props>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = mergeClassName("focus-visible:focus-ring-primary outline-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <BaseNavigationMenu.Trigger ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
const Content = forwardRef<ComponentRef<typeof BaseNavigationMenu.Content>, NavigationMenu.Content.Props>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = mergeClassName("outline-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <BaseNavigationMenu.Content ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});

/**
 * Renders an `<a>`; compose the router with `render={<LinkWithoutPrefetch href="…" />}`.
 *
 * `closeOnClick` defaults to `true` — unlike Base UI, and matching `Menu.Item` — because a client
 * side navigation would otherwise leave the popup open over the page it just left.
 */
const Link = forwardRef<ComponentRef<typeof BaseNavigationMenu.Link>, NavigationMenu.Link.Props>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      closeOnClick: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const closeOnClick = t1 === undefined ? true : t1;
  let t2;
  if ($[4] !== className) {
    t2 = mergeClassName("focus-visible:focus-ring-primary outline-none", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== closeOnClick || $[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <BaseNavigationMenu.Link ref={ref} className={t2} closeOnClick={closeOnClick} {...props} />;
    $[6] = closeOnClick;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
const Portal = forwardRef<ComponentRef<typeof BaseNavigationMenu.Portal>, NavigationMenu.Portal.Props>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <BaseNavigationMenu.Portal ref={ref} {...props} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});

/**
 * Positions the shared popup against the active trigger. `--positioner-width` / `-height` are
 * written by the active trigger while it hands the popup over, so the box tracks the incoming panel
 * instead of jumping to it.
 */
const Positioner = forwardRef<ComponentRef<typeof BaseNavigationMenu.Positioner>, NavigationMenu.Positioner.Props>((t0, ref) => {
  const $ = _c(13);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      align: t1,
      className,
      sideOffset: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const align = t1 === undefined ? "end" : t1;
  const sideOffset = t2 === undefined ? 4 : t2;
  let t3;
  if ($[5] !== className) {
    t3 = mergeClassName("h-(--positioner-height) w-(--positioner-width)", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== align || $[8] !== props || $[9] !== ref || $[10] !== sideOffset || $[11] !== t3) {
    t4 = <BaseNavigationMenu.Positioner ref={ref} align={align} className={t3} sideOffset={sideOffset} {...props} />;
    $[7] = align;
    $[8] = props;
    $[9] = ref;
    $[10] = sideOffset;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
});

/**
 * The menu popup chrome, widened for a shared popup: the active trigger writes `--popup-width` /
 * `-height` while handing the popup over, and those sizes have to be part of the transition.
 */
const POPUP_CLASS_NAME = twMerge(menuPopupVariants(), 'h-(--popup-height) w-(--popup-width)');
const Popup = forwardRef<ComponentRef<typeof BaseNavigationMenu.Popup>, NavigationMenu.Popup.Props>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = mergeClassName(POPUP_CLASS_NAME, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <BaseNavigationMenu.Popup ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
const Viewport = forwardRef<ComponentRef<typeof BaseNavigationMenu.Viewport>, NavigationMenu.Viewport.Props>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <BaseNavigationMenu.Viewport ref={ref} {...props} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
Root.displayName = 'NavigationMenu.Root';
List.displayName = BaseNavigationMenu.List.displayName;
Item.displayName = BaseNavigationMenu.Item.displayName;
Trigger.displayName = BaseNavigationMenu.Trigger.displayName;
Content.displayName = BaseNavigationMenu.Content.displayName;
Link.displayName = BaseNavigationMenu.Link.displayName;
Portal.displayName = BaseNavigationMenu.Portal.displayName;
Positioner.displayName = BaseNavigationMenu.Positioner.displayName;
Popup.displayName = BaseNavigationMenu.Popup.displayName;
Viewport.displayName = BaseNavigationMenu.Viewport.displayName;
const NavigationMenu = {
  Root,
  List,
  Item,
  Trigger,
  Content,
  Link,
  Portal,
  Positioner,
  Popup,
  Viewport
};
namespace NavigationMenu {
  export namespace Root {
    export type State = BaseNavigationMenu.Root.State;
    export type Props<Value = unknown> = BaseNavigationMenu.Root.Props<Value>;
  }
  export namespace List {
    export type Props = BaseNavigationMenu.List.Props;
  }
  export namespace Item {
    export type Props = BaseNavigationMenu.Item.Props;
  }
  export namespace Trigger {
    export type State = BaseNavigationMenu.Trigger.State;
    export type Props = BaseNavigationMenu.Trigger.Props;
  }
  export namespace Content {
    export type State = BaseNavigationMenu.Content.State;
    export type Props = BaseNavigationMenu.Content.Props;
  }
  export namespace Link {
    export type State = BaseNavigationMenu.Link.State;
    export type Props = BaseNavigationMenu.Link.Props;
  }
  export namespace Portal {
    export type Props = BaseNavigationMenu.Portal.Props;
  }
  export namespace Positioner {
    export type State = BaseNavigationMenu.Positioner.State;
    export type Props = BaseNavigationMenu.Positioner.Props;
  }
  export namespace Popup {
    export type State = BaseNavigationMenu.Popup.State;
    export type Props = BaseNavigationMenu.Popup.Props;
  }
  export namespace Viewport {
    export type Props = BaseNavigationMenu.Viewport.Props;
  }
}
export { NavigationMenu };